import React, { Component } from 'react';
import PropTypes from 'prop-types';
import React3 from 'react-three-renderer';
import ReactDOM from 'react-dom';
import {connect} from 'react-redux';
import * as THREE from 'three';
import OrbitControls from 'three-orbit-controls';

import { updateCamera } from '../../actions';
import { STACKUP_ROTATION_SPEED } from '../../constants/globals';
import MouseInput from '../../vendor/MouseInput';
import Floor from './materials/Floor';
import Resources from './materials/Resources';
import { cameraShape } from '../../lib/shapes';

export class Scene extends Component {
  constructor(props) {
    super(props);
    this._mouseUpListener = this._onMouseUp.bind(this);
    this._orbitControlsHandler = this._onControllerChange.bind(this);
    this._onAnimate = this._onAnimate.bind(this);
    this._renderTrigger = function(){}
    this._onManualRenderTriggerCreated = (renderTrigger) => {
       this._renderTrigger = renderTrigger;
       renderTrigger();
    }
    this.state = {
      stackupRotation: new THREE.Euler(),
      mouseInput: null,
    }
  }

  componentDidMount() {
    const { autoRender } = this.props;
    // eslint-disable-next-line react/no-find-dom-node
    this._canvas = ReactDOM.findDOMNode(this._react3);
    this._orbitControls = new (OrbitControls(THREE))(this._camera, this._canvas);
    this._init(autoRender);
  }

  componentWillReceiveProps(nextProps) {
    const { autoRender } =this.props;
    if(nextProps.autoRender !== autoRender) {
      this._init(nextProps.autoRender);
    }
  }

  componentDidUpdate(newProps) {
    const {
      width,
      height,
    } = this.props;

    if (width !== newProps.width || height !== newProps.height) {
      this._mouseInput.containerResized();
    }
  }

  componentWillUnmount() {
    this._orbitControls.removeEventListener('change', this._orbitControlsHandler, false);
    this._canvas.removeEventListener('mouseup', this._mouseUpListener, false);
    this._orbitControls.dispose();
  }

  _init(autoRender) {
    this._canvas.removeEventListener('mouseup', this._mouseUpListener, false);
    this._orbitControls.removeEventListener('change', this._orbitControlsHandler, false);

    if(autoRender === false){
      this._orbitControls.addEventListener('change', this._orbitControlsHandler, false);
      this._renderTrigger();
    } else {
      this._canvas.addEventListener('mouseup', this._mouseUpListener, false);
    }
  }


  _onObjMounted(layers) {
    this.layers = layers;
  }

  _onAnimateInternal() {
    const { mouseInput, camera } = this.state;

    if (!this._mouseInput.isReady()) {
      this._mouseInput.ready(this._scene, this._container, this._camera);
      this._mouseInput.restrictIntersections(this.layers, true)
      this._mouseInput.setActive(false);
    }

    if (mouseInput !== this._mouseInput) {
      this.setState({
        mouseInput: this._mouseInput,
      })
    }

    if (camera !== this._camera) {
      this.setState({
        camera: this._camera,
      })
    }
  }

  _onAnimate() {
    const { animate } = this.props;
    const { stackupRotation } = this.state;

    this._onAnimateInternal();
    if (animate) {
      this.setState({
        stackupRotation: new THREE.Euler(
          0,
          stackupRotation.y + STACKUP_ROTATION_SPEED,
          0
        )
      });
    }
  }

  _onMouseUp() {
    const { updateCamera } = this.props;

    updateCamera({
      position: this._camera.position,
      quaternion: this._camera.quaternion,
    });
  }

  _onControllerChange() {
    const { updateCamera } = this.props;

    updateCamera({
      position: this._camera.position,
      quaternion: this._camera.quaternion,
    });
  }

  render() {
    const {
      autoRender,
      axisHelper,
      camera,
      children,
      floor,
      height,
      width,
    } = this.props;
    const { stackupRotation } = this.state;
    const floorPosition = new THREE.Vector3(0, floor, 0);
    const setMouseInput = mouseInput => { this._mouseInput = mouseInput };
    const setReact3 = react3 => { this._react3 = react3 };
    const setScene = scene => { this._scene = scene };
    const setCamera = camera => { this._camera = camera };
    const cameraPosition = camera.position.isVector3 
     ? camera.position 
     : new THREE.Vector3(
      camera.position.x || 0,
      camera.position.y || 0,
      camera.position.z || 0
    );

    if (autoRender === false) {
      this._renderTrigger();
    }

    return (
      <div
        ref={(container)=>{ this._container = container; }}
      >
        <React3
          ref={setReact3}
          mainCamera="camera"
          width={width}
          height={height}
          antialias
          shadowMapEnabled
          clearColor={0xf0f0f0}
          forceManualRender={!autoRender}
          onAnimate={this._onAnimate}
          onManualRenderTriggerCreated={this._onManualRenderTriggerCreated}
        >
          <module
            ref={setMouseInput}
            descriptor={MouseInput}
          />
          <Resources />
          <scene ref={setScene}>
            <perspectiveCamera
              ref={setCamera}
              name="camera"
              fov={50}
              aspect={width/height}
              near={1}
              far={1000}
              position={cameraPosition}
            />
            <ambientLight
              color={new THREE.Color(0x333333)}
            />
            <pointLight
              color={new THREE.Color(0xFFFFFF)}
              intensity={1.5}
              position={new THREE.Vector3(0,200,200)}
            />
            {axisHelper ?  <axisHelper scale={new THREE.Vector3(100,100,100)} /> : null}
            <group
              key='fixture'
              rotation={stackupRotation}
            >
              {floor !== undefined && <Floor position={floorPosition} />}
              {React.Children.map(children, child => (
                React.cloneElement(child, {
                  mouseInput: this._mouseInput,
                  onObjMounted: this._onObjMounted.bind(this),
                })
              ))}
            </group>
          </scene>
        </React3>
      </div>
    );
  }
}

Scene.propTypes = {
  animate:        PropTypes.bool,
  autoRender:     PropTypes.bool,
  axisHelper:     PropTypes.bool,
  camera:         PropTypes.shape(cameraShape).isRequired,
  floor:          PropTypes.number,
  height:         PropTypes.number.isRequired,
  width:          PropTypes.number.isRequired,
  updateCamera:   PropTypes.func.isRequired,
  children:       PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired
}

Scene.defaultProps = {
  animate: undefined,
  autoRender: undefined,
  axisHelper: undefined,
  floor: undefined,
}

function mapStateToProps(state){
  return {
    camera:   state.camera,
  };
}

const mapDispatchToProps = {
  updateCamera
}

export default connect(mapStateToProps, mapDispatchToProps)(Scene);



// WEBPACK FOOTER //
// ./src/components/common/Scene.js